/**
 * Servizi per la **sotto-scelta concessa** da una mossa avanzata
 * (FI-31 D5 + FI-30 D6).
 *
 * Una mossa con `MoveSchema.grantsMoveChoice` concede al PG, al momento
 * della presa al level-up, la possibilità di scegliere/ricevere
 * un'opzione da un pool. Discriminated union su tre varianti:
 *
 *  - `'from-group'` (FI-31): pool = opzioni residue di uno
 *    `StartingMoveChoiceGroup` (mosse del gruppo che il PG NON ha già).
 *    Esempi: *Istinti Primordiali* del Barbaro, *Formazione Eterogenea*
 *    del Mago, *Allargare le Basi* del Chierico.
 *
 *  - `'from-choice-move'` (FI-31): pool = `choices` residue di una
 *    mossa-config (entries di `MoveSchema.choices` NON ancora in
 *    `selectedChoices` del relativo `MoveRef`). Esempi: *Ho Ancora
 *    Fame* del Barbaro (da `barbaro.appetiti-erculei`), *Aura Versatile*
 *    del Paladino, *Nel Bene o nel Male* dello Sciamano.
 *
 *  - `'from-choice-mapping'` (FI-30): pool = mosse cross-class derivate
 *    dalle `selectedChoices` del MoveRef sorgente via `mapping`. Pool
 *    deterministicamente di dimensione 0 o 1 (la sorgente ha
 *    tipicamente 1 sola choice selezionata). Caso UNICO: *Essere il
 *    Totem* del Barbaro, mappa la specie totemica scelta in
 *    `barbaro.istinto-misticismo` a una mossa di Ladro/Guerriero/Paladino.
 *
 * Le funzioni qui sono **pure** (nessun side-effect): orchestrazione +
 * applicazione transazionale al `Character` vivono in `services/levelUp.ts`
 * (`applyLevelUp`).
 *
 * @see {@link MoveGrantsMoveChoiceSchema} in `schemas/move.ts` per la
 *      semantica completa dello schema.
 * @see {@link applyLevelUp} in `services/levelUp.ts` per l'applicazione
 *      transazionale della sotto-scelta al PG.
 */

import { findMoveById, findMoveByIdGlobal } from '../data/moves';
import type { Character } from '../schemas/character';
import type { ClassDefinition } from '../schemas/classDefinition';
import type { ClassId } from '../schemas/classId';
import type { MoveGrantsMoveChoice } from '../schemas/move';
import { findMoveRefInCharacter } from './moveLookup';

/**
 * Pool residuo per la sotto-scelta concessa da `grants`.
 *
 *  - `'from-group'`: ritorna gli ID-mossa del gruppo iniziale (dato
 *    `groupId`) NON ancora presenti in `character.moves`. Se il gruppo
 *    non esiste in `classDef.startingChoiceGroups`, ritorna lista vuota.
 *  - `'from-choice-move'`: ritorna gli ID delle `MoveChoice` della
 *    mossa-config sorgente NON ancora selezionati dal PG (controlla sia
 *    `selectedChoices` shorthand che `selectedChoiceGroups.main` proietta).
 *    Se la mossa-config non è nelle `character.moves` (caso patologico
 *    che NON dovrebbe accadere: il prerequisito della mossa avanzata
 *    garantisce che la sorgente sia presente), ritorna lista vuota.
 *  - `'from-choice-mapping'` (FI-30): ritorna 0..N ID-mossa cross-class
 *    derivate applicando `mapping` alle `selectedChoices` del MoveRef
 *    sorgente. Esclude mosse cross-class già possedute dal PG
 *    (idempotenza). Lista vuota se: sorgente assente da `character.moves`,
 *    nessuna choice selezionata, choice senza mapping definito, o tutte
 *    le mosse target già possedute.
 *
 * @returns array di ID stringa (mosse o choice-id). Lista vuota = la mossa
 *          avanzata con questa grant NON è selezionabile al level-up
 *          (vedi filtro in `getAvailableAdvancedMoves`).
 */
export function getGrantedPool(
  character: Character,
  grants: MoveGrantsMoveChoice,
  classDef: ClassDefinition,
): readonly string[] {
  if (grants.kind === 'from-group') {
    return getFromGroupPool(character, grants.groupId, classDef);
  }
  if (grants.kind === 'from-choice-move') {
    return getFromChoiceMovePool(character, grants.moveId, classDef.id);
  }
  if (grants.kind === 'from-list') {
    return getFromListPool(character, grants.moveOptions);
  }
  return getFromChoiceMappingPool(character, grants.sourceMoveId, grants.mapping);
}

/**
 * Dimensione del pool residuo. Helper di convenienza per check di
 * disponibilità della mossa (vedi `isAdvancedMoveAvailable`).
 */
export function getGrantedPoolSize(
  character: Character,
  grants: MoveGrantsMoveChoice,
  classDef: ClassDefinition,
): number {
  return getGrantedPool(character, grants, classDef).length;
}

function getFromGroupPool(
  character: Character,
  groupId: string,
  classDef: ClassDefinition,
): readonly string[] {
  const group = classDef.startingChoiceGroups.find((g) => g.id === groupId);
  if (!group) return [];
  const ownedIds = new Set(character.moves.map((m) => m.id));
  return group.moveOptions.filter((id) => !ownedIds.has(id));
}

/**
 * **FI-82 (2026-05-24)** — Pool della grant `from-list`.
 *
 * Risoluzione: filtra le `moveOptions` inline rimuovendo quelle già
 * possedute dal PG (idempotenza). Niente lookup esterno (`classDef`,
 * `startingChoiceGroups`) — tutto il dato vive nella grant.
 *
 * Lista vuota se tutte le opzioni sono già possedute → la mossa con
 * questa grant viene filtrata fuori da `getAvailableAdvancedMoves`.
 */
function getFromListPool(character: Character, moveOptions: readonly string[]): readonly string[] {
  const ownedIds = new Set(character.moves.map((m) => m.id));
  return moveOptions.filter((id) => !ownedIds.has(id));
}

function getFromChoiceMovePool(
  character: Character,
  moveId: string,
  classId: ClassId,
): readonly string[] {
  const ref = findMoveRefInCharacter(character, moveId);
  if (!ref) return [];
  const move = findMoveById(classId, moveId);
  if (!move) return [];
  const allChoiceIds = (move.choices ?? []).map((c) => c.id);
  if (allChoiceIds.length === 0) return [];
  // Proietta sia shorthand single-group (`selectedChoices`) che multi-group
  // virtuale (`selectedChoiceGroups.main`) per coerenza col pattern UI.
  const selected = new Set<string>([
    ...ref.selectedChoices,
    ...(ref.selectedChoiceGroups?.main ?? []),
  ]);
  return allChoiceIds.filter((id) => !selected.has(id));
}

/**
 * **FI-30 (D6)**: pool della grant `from-choice-mapping`.
 *
 * Risoluzione:
 *  1. `character.moves` contiene la mossa-config `sourceMoveId`? (NB:
 *     `findMoveByIdGlobal` per essere coerenti con altri grant cross-class,
 *     anche se in tutti i casi noti la mossa sorgente è della stessa
 *     classe del PG).
 *  2. Estrae le `selectedChoices` del MoveRef sorgente (proietta sia
 *     shorthand `selectedChoices` che `selectedChoiceGroups.main` per
 *     coerenza con `from-choice-move`).
 *  3. Per ogni choice selezionata, lookup `mapping[choiceId]`.
 *  4. Filtra mosse target già possedute dal PG (idempotenza).
 *  5. Dedupe (caso patologico: stessa mossa target in più mapping
 *     entries).
 *
 * Lista vuota se: sorgente assente, nessuna choice selezionata, choice
 * senza entry in mapping, o tutte le mosse target già possedute. In
 * questi casi la mossa avanzata con questa grant viene filtrata fuori
 * da `getAvailableAdvancedMoves`.
 */
function getFromChoiceMappingPool(
  character: Character,
  sourceMoveId: string,
  mapping: Readonly<Record<string, string>>,
): readonly string[] {
  const ref = findMoveRefInCharacter(character, sourceMoveId);
  if (!ref) return [];
  const sourceMove = findMoveByIdGlobal(sourceMoveId);
  if (!sourceMove) return [];
  // Selezioni della mossa sorgente (shorthand + main virtuale).
  const selected = new Set<string>([
    ...ref.selectedChoices,
    ...(ref.selectedChoiceGroups?.main ?? []),
  ]);
  if (selected.size === 0) return [];
  const ownedMoveIds = new Set(character.moves.map((m) => m.id));
  const resolved: string[] = [];
  const seen = new Set<string>();
  for (const choiceId of selected) {
    const targetMoveId = mapping[choiceId];
    if (targetMoveId === undefined) continue;
    if (ownedMoveIds.has(targetMoveId)) continue;
    if (seen.has(targetMoveId)) continue;
    seen.add(targetMoveId);
    resolved.push(targetMoveId);
  }
  return resolved;
}
